import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCart } from '../../context/CartContext';

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-PR', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const isVideoUrl = (url = '') => /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(url);

const EcommerceLanding = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeImageMap, setActiveImageMap] = useState({});
  const [variantSelections, setVariantSelections] = useState({});
  const [addonSelections, setAddonSelections] = useState({});
  const [activeCategoryId, setActiveCategoryId] = useState('');
  const [activeSubcategoryId, setActiveSubcategoryId] = useState('');
  const [addonCategories, setAddonCategories] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesSnap, productsSnap, addonCategoriesSnap] = await Promise.all([
          getDocs(collection(db, 'ecommerce_categories')),
          getDocs(collection(db, 'ecommerce_products')),
          getDocs(collection(db, 'ecommerce_addon_categories')),
        ]);
        const addonCategoriesData = addonCategoriesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCategories(categoriesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setProducts(
          productsSnap.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((product) => product.visible !== false)
        );
        setAddonCategories(addonCategoriesData);
      } catch (error) {
        console.error('❌ Error loading ecommerce data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!categories.length) return;
    if (!activeCategoryId) {
      setActiveCategoryId(categories[0].id);
      setActiveSubcategoryId('');
    }
  }, [categories, activeCategoryId]);

  const handleImageChange = (productId, index) => {
    setActiveImageMap((prev) => ({ ...prev, [productId]: index }));
  };

  const handleVariantSelect = (productId, attribute, value) => {
    setVariantSelections((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [attribute]: value },
    }));
  };

  const addonCategoryLookup = useMemo(() => {
    const byCategoryId = {};
    const byItemId = {};
    const byItemName = {};
    addonCategories.forEach((category) => {
      if (category?.id) {
        byCategoryId[category.id] = category.name || '';
      }
      (category?.items || []).forEach((item) => {
        if (item?.id) {
          byItemId[item.id] = category.name || '';
        }
        if (item?.name && !byItemName[item.name]) {
          byItemName[item.name] = category.name || '';
        }
      });
    });
    return { byCategoryId, byItemId, byItemName };
  }, [addonCategories]);

  const resolveAddonCategory = (addon) => {
    if (!addon) return '';
    if (addon.categoryName) return addon.categoryName;
    if (addon.categoryId && addonCategoryLookup.byCategoryId[addon.categoryId]) {
      return addonCategoryLookup.byCategoryId[addon.categoryId];
    }
    if (addon.id && addonCategoryLookup.byItemId[addon.id]) {
      return addonCategoryLookup.byItemId[addon.id];
    }
    if (addon.name && addonCategoryLookup.byItemName[addon.name]) {
      return addonCategoryLookup.byItemName[addon.name];
    }
    return '';
  };

  const getAddonKey = (addon) => {
    const category = resolveAddonCategory(addon);
    return addon?.id || `${addon?.categoryId || category || addon?.categoryName || 'addon'}:${addon?.name || 'item'}`;
  };

  const getAddonLabel = (addon) => {
    const base = addon?.name || '';
    return base;
  };

  const handleAddonQuantityChange = (productId, addon, nextQuantity) => {
    const qty = Math.max(0, Number(nextQuantity) || 0);
    setAddonSelections((prev) => {
      const current = { ...(prev[productId] || {}) };
      const key = getAddonKey(addon);
      if (!qty) {
        delete current[key];
      } else {
        current[key] = qty;
      }
      return { ...prev, [productId]: current };
    });
  };

  const getVariantSelection = (productId, attribute) =>
    variantSelections[productId]?.[attribute] || '';

  const getAddonQuantity = (productId, addon) =>
    addonSelections[productId]?.[getAddonKey(addon)] || 0;

  const buildVariantDetails = (product) => {
    const selections = variantSelections[product.id] || {};
    return (product.variants || []).map((variant) => {
      const chosen =
        variant.values?.find((value) => value.value === selections[variant.attribute]) ||
        variant.values?.[0] ||
        {};
      return {
        attribute: variant.attribute,
        value: chosen.value || '',
        priceAdjustment: chosen.priceAdjustment || 0,
      };
    });
  };

  const buildAddonDetails = (product) => {
    const selectedMap = addonSelections[product.id] || {};
    return (product.addons || [])
      .map((addon) => ({
        key: getAddonKey(addon),
        addon,
        quantity: selectedMap[getAddonKey(addon)] || 0,
      }))
      .filter((entry) => entry.quantity > 0)
      .map(({ addon, quantity }) => ({
        id: addon.id || '',
        name: addon.name,
        categoryName: resolveAddonCategory(addon),
        price: parseFloat(addon.price) || 0,
        quantity,
      }));
  };

  useEffect(() => {
    if (!selectedProduct) return;
    setVariantSelections((prev) => {
      const existing = prev[selectedProduct.id] || {};
      let updated = existing;
      let changed = false;
      (selectedProduct.variants || []).forEach((variant) => {
        const firstValue = variant.values?.[0]?.value;
        if (variant.attribute && firstValue && !existing[variant.attribute]) {
          updated = { ...updated, [variant.attribute]: firstValue };
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, [selectedProduct.id]: updated };
    });
  }, [selectedProduct]);

  const handleAddToCart = () => {
    if (!selectedProduct) return;
    const variantDetails = buildVariantDetails(selectedProduct);
    const addonDetails = buildAddonDetails(selectedProduct);
    const basePrice = parseFloat(selectedProduct.basePrice) || 0;
    const variantTotal = variantDetails.reduce((sum, entry) => sum + (entry.priceAdjustment || 0), 0);
    const addonTotal = addonDetails.reduce((sum, entry) => sum + (entry.price || 0) * entry.quantity, 0);
    const totalPrice = basePrice + variantTotal + addonTotal;

    addItem({
      productId: selectedProduct.id,
      name: selectedProduct.name,
      basePrice,
      variants: variantDetails,
      addons: addonDetails,
      image: selectedProduct.gallery?.[0] || '',
      quantity: 1,
      totalPrice,
    });
    setSelectedProduct(null);
  };

  const renderMedia = (
    url,
    { className = '', controls = false, muted = false, autoPlay = false, alt = '' } = {}
  ) => {
    if (!url) return null;
    if (isVideoUrl(url)) {
      return (
        <video
          src={url}
          className={className}
          controls={controls}
          muted={muted}
          autoPlay={autoPlay}
          loop={autoPlay}
          playsInline
          preload="metadata"
        />
      );
    }
    return <img src={url} alt={alt} className={className} loading="lazy" />;
  };

  const activeCategory = categories.find((category) => category.id === activeCategoryId) || null;
  const activeSubcategories = activeCategory?.subcategories || [];
  const filteredProducts = products.filter((product) => {
    if (activeCategoryId && product.categoryId !== activeCategoryId) return false;
    if (activeSubcategoryId && product.subcategoryId !== activeSubcategoryId) return false;
    return true;
  });

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-700">Puerto Rico Windows & Doors</p>
            <h1 className="text-3xl font-bold text-gray-900">Tienda en línea</h1>
            <p className="text-sm text-gray-500">
              Explora sistemas de ventanas, puertas y accesorios con precios base. Los montos finales se confirman con
              visita técnica.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="/"
              className="px-4 py-2 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 text-center sm:text-left"
            >
              Volver al inicio
            </a>
            <a
              href="#contact"
              className="px-4 py-2 rounded-full bg-emerald-600 text-sm font-semibold text-white shadow hover:bg-emerald-700 text-center sm:text-left"
            >
              Solicitar cotización
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-12">
        <section>
          <p className="text-xs uppercase tracking-[0.4em] text-gray-500 mb-3">Categorías</p>
          <div className="flex gap-3 overflow-x-auto pb-2 sm:pb-0 sm:grid sm:grid-cols-3">
            {categories.map((category) => {
              const isActive = category.id === activeCategoryId;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => {
                    setActiveCategoryId(category.id);
                    setActiveSubcategoryId('');
                  }}
                  className={`min-w-[220px] rounded-2xl border bg-white p-4 shadow-sm space-y-1 flex-shrink-0 text-left transition ${
                    isActive ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-emerald-100'
                  }`}
                >
                  <p className="text-sm font-semibold text-emerald-700">{category.name}</p>
                  <p className="text-xs text-gray-500">{category.description}</p>
                  {category.subcategories?.length > 0 && (
                    <p className="text-xs text-gray-600">
                      {category.subcategories.length} subcategorías
                    </p>
                  )}
                </button>
              );
            })}
            {categories.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Aún no hay categorías publicadas.</p>
            )}
          </div>

          {activeCategory && activeSubcategories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveSubcategoryId('')}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                  !activeSubcategoryId ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600'
                }`}
              >
                Todas
              </button>
              {activeSubcategories.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => setActiveSubcategoryId(sub.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    activeSubcategoryId === sub.id
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Modelos</p>
              <h2 className="text-2xl font-bold text-gray-900">Productos destacados</h2>
            </div>
            <span className="text-sm text-gray-500">{filteredProducts.length} resultados</span>
          </div>
          {loading && <p className="text-sm text-gray-500">Cargando inventario...</p>}
          {!loading && filteredProducts.length === 0 && (
            <p className="text-sm text-gray-500">No hay productos visibles por el momento.</p>
          )}
          <div className="grid gap-6 sm:grid-cols-2">
            {filteredProducts.map((product) => (
              <article
                key={product.id}
                className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col"
              >
                <div className="relative h-56 w-full overflow-hidden bg-gray-100">
                  {product.gallery?.length ? (
                    renderMedia(product.gallery[0], {
                      className: 'absolute inset-0 h-full w-full object-cover',
                      muted: true,
                      alt: product.name || '',
                    })
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                      Foto no disponible
                    </div>
                  )}
                  <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-emerald-700 shadow">
                    Stock rápido
                  </span>
                </div>
                <div className="p-6 space-y-3 flex-1 flex flex-col">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-xl font-semibold text-gray-900">{product.name}</h3>
                    <span className="text-lg font-bold text-emerald-700">{formatCurrency(product.basePrice)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {activeCategory?.name && (
                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">
                        {activeCategory.name}
                      </span>
                    )}
                    {activeSubcategoryId && (
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-600">
                        {activeSubcategories.find((sub) => sub.id === activeSubcategoryId)?.name || 'Subcategoría'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-3">{product.description}</p>
                  <div className="mt-auto">
                    <button
                      onClick={() => setSelectedProduct(product)}
                      className="w-full rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Ver detalles
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>

      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="relative h-56 w-full overflow-hidden bg-gray-100">
              {selectedProduct.gallery?.length ? (
                renderMedia(selectedProduct.gallery[activeImageMap[selectedProduct.id] || 0], {
                  className: 'absolute inset-0 h-full w-full object-cover',
                  controls: true,
                  alt: selectedProduct.name || '',
                })
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                  Foto no disponible
                </div>
              )}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-sm font-semibold text-gray-700 shadow"
              >
                Cerrar
              </button>
            </div>
            {selectedProduct.gallery?.length > 1 && (
              <div className="flex gap-2 px-6 pt-4">
                {selectedProduct.gallery.slice(0, 5).map((image, idx) => (
                  <button
                    key={`${selectedProduct.id}-thumb-${idx}`}
                    onClick={() => handleImageChange(selectedProduct.id, idx)}
                    className={`h-16 w-16 overflow-hidden rounded-xl border ${
                      (activeImageMap[selectedProduct.id] || 0) === idx ? 'border-emerald-500' : 'border-gray-200'
                    }`}
                    aria-label={`Cambiar a imagen ${idx + 1}`}
                  >
                    {renderMedia(image, {
                      className: 'h-full w-full object-cover',
                      muted: true,
                      alt: `${selectedProduct.name || 'producto'}-${idx}`,
                    })}
                  </button>
                ))}
              </div>
            )}
            <div className="p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Modelo</p>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedProduct.name}</h3>
                </div>
                <span className="text-xl font-bold text-emerald-700">
                  {formatCurrency(selectedProduct.basePrice)}
                </span>
              </div>
              <p className="text-sm text-gray-600">{selectedProduct.description}</p>
              {Array.isArray(selectedProduct.variants) && selectedProduct.variants.length > 0 && (
                <div className="space-y-4 text-sm text-gray-700">
                  {selectedProduct.variants.map((variant) => (
                    <div key={`${selectedProduct.id}-${variant.attribute}`}>
                      <p className="font-semibold mb-2">{variant.attribute}</p>
                      <div className="flex flex-wrap gap-2">
                        {(variant.values || []).map((value) => {
                          const selectedOption =
                            getVariantSelection(selectedProduct.id, variant.attribute) === value.value;
                          return (
                            <label
                              key={`${selectedProduct.id}-${variant.attribute}-${value.value}`}
                              className={`cursor-pointer rounded-2xl border px-4 py-2 text-xs font-semibold flex items-center gap-2 ${
                                selectedOption
                                  ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                                  : 'border-gray-200 text-gray-600 hover:border-emerald-400'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`variant-${selectedProduct.id}-${variant.attribute}`}
                                className="sr-only"
                                checked={selectedOption}
                                onChange={() =>
                                  handleVariantSelect(selectedProduct.id, variant.attribute, value.value)
                                }
                              />
                              <span>
                                {value.value}{' '}
                                {value.priceAdjustment
                                  ? `(${value.priceAdjustment > 0 ? '+' : ''}${formatCurrency(value.priceAdjustment)})`
                                  : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(selectedProduct.addons) && selectedProduct.addons.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-2">Accesorios disponibles:</p>
                  <div className="grid gap-2 sm:grid-cols-2 text-sm text-gray-600">
                    {selectedProduct.addons.map((addon) => {
                      const qty = getAddonQuantity(selectedProduct.id, addon);
                      const categoryName = resolveAddonCategory(addon);
                      return (
                        <div
                          key={`${selectedProduct.id}-modal-addon-${getAddonKey(addon)}`}
                          className="rounded-2xl border px-3 py-2 text-xs font-semibold flex items-center justify-between gap-2"
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-gray-800">{getAddonLabel(addon)}</span>
                            {categoryName ? (
                              <span className="text-[10px] uppercase tracking-wide text-emerald-700">
                                {categoryName}
                              </span>
                            ) : null}
                            {addon.price ? (
                              <span className="text-[11px] text-gray-500">{formatCurrency(addon.price)}</span>
                            ) : null}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400">Cantidad</span>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleAddonQuantityChange(selectedProduct.id, addon, qty - 1)}
                                className="h-7 w-7 rounded-full border border-gray-200 text-gray-600"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) =>
                                  handleAddonQuantityChange(selectedProduct.id, addon, e.target.value)
                                }
                                className="w-12 rounded-xl border border-gray-200 px-2 py-1 text-center text-xs"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddonQuantityChange(selectedProduct.id, addon, qty + 1)}
                                className="h-7 w-7 rounded-full border border-gray-200 text-gray-600"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
                >
                  Agregar al carrito
                </button>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Seguir explorando
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default EcommerceLanding;
